import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useContentLibrary } from "@/data/content";

const assetUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  return `${process.env.PUBLIC_URL || ""}${path.startsWith("/") ? path : `/${path}`}`;
};

const fallbackHero = {
  eyebrow: "New Launch · AI Compliance Governance",
  titleLines: ["Govern AI.", "Master Compliance."],
  highlightLine: 1,
  description:
    "Jaisphere introduces the enterprise standard for AI Inventory and Compliance Governance — paired with two decades of cloud-native engineering. De-risk your workflows in minutes, not months.",
  proofPoints: ["SOC 2 · ISO 27001 ready", "Founded 2008 · Atlanta, GA"],
  image: "/assets/ai-inventory-compliance-governance/hero.png",
  primaryCta: "Explore the Product",
  secondaryCta: "Book a Demo",
};

export const Hero = () => {
  const { data } = useContentLibrary();
  const flagship = data.products.find((product) => product.flagship) || data.products[0];
  const hero = flagship?.hero || fallbackHero;
  const productPath = flagship ? `/products/${flagship.slug}` : "/products";

  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 grid-backdrop pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid grid-cols-12 gap-8 items-center">
        {/* Text */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="col-span-12 lg:col-span-7"
        >
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]"
          >
            <Sparkles size={14} />
            {hero.eyebrow}
          </span>

          <h1
            data-testid="hero-headline"
            className="mt-5 font-display font-black tracking-tighter leading-[0.95] text-zinc-950 dark:text-zinc-50 text-5xl sm:text-6xl lg:text-7xl"
          >
            {hero.titleLines.map((line, index) => (
              <React.Fragment key={`${line}-${index}`}>
                {index > 0 && <br />}
                {Number(hero.highlightLine) === index ? (
                  <span className="text-[#002FA7] dark:text-[#7C9BFF]">{line}</span>
                ) : (
                  line
                )}
              </React.Fragment>
            ))}
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
            {hero.description}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to={productPath}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white px-6 py-3.5 rounded-sm text-sm font-semibold transition-colors"
            >
              {hero.primaryCta}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/#contact"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white dark:border-zinc-200 dark:text-zinc-50 dark:hover:bg-zinc-50 dark:hover:text-zinc-950 px-6 py-3.5 rounded-sm text-sm font-semibold transition-colors"
            >
              {hero.secondaryCta}
            </Link>
          </div>

          {hero.proofPoints.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-5 text-xs text-zinc-500 dark:text-zinc-400">
              {hero.proofPoints.map((point) => (
                <span key={point} className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-[#002FA7]" />
                  {point}
                </span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          className="col-span-12 lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-2 bg-[#002FA7]/5 dark:bg-[#7C9BFF]/10 rounded-sm" />
            <div className="relative border border-zinc-200 bg-white rounded-sm overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,47,167,0.25)] dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-[0_30px_80px_-30px_rgba(124,155,255,0.25)]">
              <img
                src={assetUrl(hero.image)}
                alt={flagship ? `${flagship.title} product preview` : "Jaisphere product preview"}
                className="w-full aspect-[4/3] object-contain object-center bg-zinc-50 dark:bg-zinc-900"
              />
            </div>
            <span className="absolute -bottom-3 -right-3 inline-flex items-center font-mono text-[10px] uppercase tracking-widest text-white bg-[#002FA7] px-2.5 py-1">
              Flagship Product
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;